import { ChevronDown, ChevronUp } from 'lucide-react'
import { Button, Label } from '@/components/ui'
import type { ComboMainMeal as ComboMainMealType, ComboRow, ComboRowIngredient, MenuItem } from '@/types/menu'
import type { SelectedModifier, IngredientVariationSelection } from '@/types/cart'
import type { RuleEvaluationResult } from '@/types/logicRules'
import { isRowVisible, isIngredientEnabled, getSelectionLimit } from '@/utils/ruleEngine'
import { rowHasDynamicPricing, resolveIngredientPrice } from '@/utils/ingredientPricing'
import { formatPrice } from '@/utils/formatting'
import { QuantityRow } from './QuantityRow'

interface ComboMainMealProps {
  mainMeal: ComboMainMealType
  customizations: SelectedModifier[]
  onCustomizationChange: (customizations: SelectedModifier[]) => void
  ingredientVariationSelections: IngredientVariationSelection[]
  onIngredientVariationChange: (selections: IngredientVariationSelection[]) => void
  ruleResult: RuleEvaluationResult
  getIngredientsToDisplay: (row: ComboRow) => ComboRowIngredient[]
  expanded: boolean
  onExpandedChange: (expanded: boolean) => void
  menuItem: MenuItem
}

export function ComboMainMeal({
  mainMeal,
  customizations,
  onCustomizationChange,
  ingredientVariationSelections,
  onIngredientVariationChange,
  ruleResult,
  getIngredientsToDisplay,
  expanded,
  onExpandedChange,
  menuItem,
}: ComboMainMealProps) {
  // Handle selecting/changing a variation for a row ingredient
  const handleVariationSelect = (
    row: ComboRow,
    ing: ComboRowIngredient,
    variationId: string,
    variationName: string,
    priceAdjustment: number
  ) => {
    const vg = ing.ingredient.variationGroup!
    // Replace any existing variation for this row ingredient
    const updated = ingredientVariationSelections.filter(
      (vs) => !(vs.rowId === row.id && vs.rowIngredientId === ing.id)
    )
    updated.push({
      rowId: row.id,
      rowIngredientId: ing.id,
      ingredientName: ing.ingredient.name,
      variationGroupId: vg.id,
      variationGroupName: vg.name,
      variationId,
      variationName,
      priceAdjustment,
    })
    onIngredientVariationChange(updated)
  }

  // Auto-select default variation when an ingredient with variations is selected
  const handleIngredientSelect = (
    row: ComboRow,
    ing: ComboRowIngredient,
    newCustomizations: SelectedModifier[]
  ) => {
    onCustomizationChange(newCustomizations)

    // Check if this ingredient was just selected (not deselected)
    const wasSelected = customizations.some(
      (m) => m.groupId === row.id && m.optionId === ing.id
    )
    if (!wasSelected && ing.ingredient.hasVariations && ing.ingredient.variationGroup) {
      const defaultVar = ing.ingredient.variationGroup.variations.find((v) => v.is_default)
      if (defaultVar) {
        handleVariationSelect(row, ing, defaultVar.id, defaultVar.name, defaultVar.price ?? 0)
      }
    }
  }

  // Compute display price for a combo row ingredient
  const getIngredientDisplayPrice = (
    row: ComboRow,
    ing: ComboRowIngredient
  ): { label: string; value: number } | null => {
    const isDynamic = rowHasDynamicPricing(menuItem, row.id)
    if (!isDynamic) return null // Static rows use ing.additionalPrice directly

    // Override ingredients always show their price
    const overridePrice = menuItem.customizationPrices?.[ing.id]
    if (overridePrice !== undefined) {
      return { label: `+${formatPrice(overridePrice, menuItem.currency, false)}`, value: overridePrice }
    }

    // Count non-override selections to determine position
    const selectedInRow = customizations.filter((m) => m.groupId === row.id)
    const nonOverrideSelected = selectedInRow.filter(
      (m) => !menuItem.customizationPrices?.[m.optionId]
    )
    const isSelected = nonOverrideSelected.some((m) => m.optionId === ing.id)
    const currentIndex = isSelected
      ? nonOverrideSelected.findIndex((m) => m.optionId === ing.id)
      : nonOverrideSelected.length

    const price = resolveIngredientPrice(menuItem, row.id, ing.id, currentIndex)
    if (price === 0) return { label: 'Included', value: 0 }
    return { label: `+${formatPrice(price, menuItem.currency, false)}`, value: price }
  }

  const renderMainMealRow = (row: ComboRow) => {
    // Check if row should be visible based on rules
    if (!isRowVisible(row.id, ruleResult, true)) {
      return null
    }

    // Get ingredients based on active variation
    const displayIngredients = getIngredientsToDisplay(row)
    const selectedIngredients = customizations.filter((m) => m.groupId === row.id)
    const selectedCount = selectedIngredients.length
    // Get dynamic max selections from rules or use row default
    const maxSelections = getSelectionLimit(row.id, ruleResult, row.maxSelections)
    const maxReached = selectedCount >= maxSelections
    const isDynamic = rowHasDynamicPricing(menuItem, row.id)
    const freeQuantity = menuItem.rowPricingRules?.[row.id]?.freeQuantity ?? 0
    const pricingLabel = menuItem.rowPricingRules?.[row.id]?.label

    // Quantity mode: render stepper UI
    if (row.selectionType === 'quantity') {
      const qtyMap: Record<string, number> = {}
      for (const mod of selectedIngredients) {
        qtyMap[mod.optionId] = mod.quantity ?? 1
      }

      const qtyIngredients = displayIngredients.map((ing) => {
        const dynamicPrice = getIngredientDisplayPrice(row, ing)
        return {
          id: ing.id,
          name: ing.ingredient.name,
          price: dynamicPrice ? dynamicPrice.value : ing.additionalPrice,
          isEnabled: isIngredientEnabled(row.id, ing.id, ruleResult, true),
        }
      })

      const handleQtyChange = (ingredientId: string, newQty: number) => {
        const ing = displayIngredients.find((i) => i.id === ingredientId)
        if (!ing) return

        const storedPrice = isDynamic ? 0 : ing.additionalPrice
        let updated = customizations.filter(
          (m) => !(m.groupId === row.id && m.optionId === ingredientId)
        )
        if (newQty > 0) {
          updated = [
            ...updated,
            {
              groupId: row.id,
              groupName: row.title,
              optionId: ingredientId,
              optionName: ing.ingredient.name,
              priceAdjustment: storedPrice,
              quantity: newQty,
            },
          ]
        }
        onCustomizationChange(updated)
      }

      return (
        <QuantityRow
          key={row.id}
          rowTitle={row.title}
          minSelections={row.minSelections}
          maxSelections={maxSelections}
          maxQuantityPerItem={row.maxQuantityPerItem ?? null}
          ingredients={qtyIngredients}
          quantities={qtyMap}
          onQuantityChange={handleQtyChange}
          freeQuantity={freeQuantity}
          isDynamic={isDynamic}
          pricingLabel={pricingLabel}
        />
      )
    }

    return (
      <div key={row.id} className="space-y-2">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            <Label className="text-sm font-medium text-muted-foreground">
              {row.title}
              {row.minSelections > 0 && <span className="text-destructive ml-1">*</span>}
            </Label>
            {isDynamic && freeQuantity > 0 && (
              <span className="text-[11px] text-primary font-medium">
                ({Math.min(freeQuantity, selectedCount)} of {freeQuantity} free)
              </span>
            )}
            {pricingLabel && (
              <span className="text-[11px] text-muted-foreground font-medium">
                · {pricingLabel}
              </span>
            )}
            {row.minSelections > 0 && selectedCount < row.minSelections && (
              <span className="text-[11px] text-destructive font-medium">
                Select at least {row.minSelections}
              </span>
            )}
          </div>
          <span className="text-xs text-muted-foreground">
            ({selectedCount}/{maxSelections})
          </span>
        </div>
        <div className="flex flex-wrap gap-2">
          {displayIngredients.map((ing) => {
            const isSelected = selectedIngredients.some((m) => m.optionId === ing.id)
            // Check if ingredient is disabled by rules
            const isDisabledByRule = !isIngredientEnabled(row.id, ing.id, ruleResult, true)
            const isDisabled = isDisabledByRule || (!isSelected && maxReached)
            const dynamicPrice = getIngredientDisplayPrice(row, ing)
            // For dynamic rows, store 0 — the hook calculates the real price
            const storedPrice = isDynamic ? 0 : ing.additionalPrice

            return (
              <Button
                key={ing.id}
                variant={isSelected ? 'black' : 'outline'}
                className={`px-3 py-1.5 h-auto rounded-full text-xs ${
                  isDisabled && !isSelected
                    ? 'bg-muted text-muted-foreground border-border'
                    : ''
                }`}
                disabled={isDisabled}
                onClick={() => {
                  if (isDisabled) return
                  // Toggle selection for multi-select
                  if (isSelected) {
                    // Deselect — also removes variation selection via pruning effect
                    onCustomizationChange(
                      customizations.filter(
                        (m) => !(m.groupId === row.id && m.optionId === ing.id)
                      )
                    )
                  } else if (!maxReached) {
                    // Select — auto-selects default variation if applicable
                    handleIngredientSelect(row, ing, [
                      ...customizations,
                      {
                        groupId: row.id,
                        groupName: row.title,
                        optionId: ing.id,
                        optionName: ing.ingredient.name,
                        priceAdjustment: storedPrice,
                      },
                    ])
                  }
                }}
              >
                {ing.ingredient.name}
                {dynamicPrice ? (
                  dynamicPrice.value !== 0 ? (
                    <span className="ml-1">{dynamicPrice.label}</span>
                  ) : (
                    <span className="ml-1 text-primary">Included</span>
                  )
                ) : (
                  ing.additionalPrice > 0 && (
                    <span className="ml-1">+{formatPrice(ing.additionalPrice, menuItem.currency, false)}</span>
                  )
                )}
              </Button>
            )
          })}
        </div>
        {/* Inline variation selectors for selected ingredients with variations */}
        {displayIngredients
          .filter(
            (ing) =>
              ing.ingredient.hasVariations &&
              ing.ingredient.variationGroup &&
              selectedIngredients.some((m) => m.optionId === ing.id)
          )
          .map((ing) => {
            const vg = ing.ingredient.variationGroup!
            const currentSelection = ingredientVariationSelections.find(
              (vs) => vs.rowId === row.id && vs.rowIngredientId === ing.id
            )
            return (
              <div key={`var-${ing.id}`} className="ml-4 pl-3 border-l-2 border-border">
                <span className="text-xs text-muted-foreground mb-1 block">
                  {ing.ingredient.name} — {vg.name}
                </span>
                <div className="flex flex-wrap gap-1.5">
                  {vg.variations
                    .slice()
                    .sort((a, b) => a.sort_order - b.sort_order)
                    .map((v) => {
                      const isVarSelected = currentSelection?.variationId === v.id
                      return (
                        <Button
                          key={v.id}
                          variant={isVarSelected ? 'default' : 'outline'}
                          className={`px-2.5 py-1 h-auto rounded-full text-[11px] ${
                            isVarSelected ? 'bg-primary text-primary-foreground border-primary' : ''
                          }`}
                          onClick={() =>
                            handleVariationSelect(row, ing, v.id, v.name, v.price ?? 0)
                          }
                        >
                          {v.name}
                          {v.price !== undefined && v.price > 0 && (
                            <span className="ml-0.5">{formatPrice(v.price, menuItem.currency, false)}</span>
                          )}
                        </Button>
                      )
                    })}
                </div>
              </div>
            )
          })}
      </div>
    )
  }

  return (
    <div className="space-y-3 border rounded-lg p-4 bg-muted/20">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <span className="text-xl">&#127828;</span>
          <div>
            <Label className="text-base font-semibold">Main Item</Label>
            <p className="text-sm text-muted-foreground">{mainMeal.title}</p>
          </div>
        </div>
        <Button
          variant="ghost"
          size="sm"
          onClick={() => onExpandedChange(!expanded)}
          className="text-sm"
        >
          {expanded ? (
            <>
              Collapse <ChevronUp className="ml-1 h-4 w-4" />
            </>
          ) : (
            <>
              Customize <ChevronDown className="ml-1 h-4 w-4" />
            </>
          )}
        </Button>
      </div>

      {/* Show current selections summary when collapsed */}
      {!expanded && customizations.length > 0 && (
        <div className="text-sm text-muted-foreground pl-9">
          {customizations.map((mod, idx) => {
            const varSel = ingredientVariationSelections.find(
              (vs) => vs.rowId === mod.groupId && vs.rowIngredientId === mod.optionId
            )
            const qty = mod.quantity ?? 1
            return (
              <span key={mod.optionId}>
                {mod.groupName}: {qty > 1 ? `${qty}x ` : ''}{mod.optionName}
                {varSel && ` (${varSel.variationName})`}
                {idx < customizations.length - 1 && ' • '}
              </span>
            )
          })}
        </div>
      )}

      {/* Expanded: Show row customizations */}
      {expanded && mainMeal.rows && mainMeal.rows.length > 0 && (
        <div className="space-y-4 pt-2 border-t mt-3">
          {[...mainMeal.rows]
            .sort((a, b) => a.order - b.order)
            .map((row) => renderMainMealRow(row))}
        </div>
      )}
    </div>
  )
}
